import { ReactNode, useEffect, useState } from "react";

import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";

import { useAppSelector } from "app/hooks";
import CircularProgress from "components/circular-progress";
import { RECORDING_STATE_IN_PROGRESS } from "features/medical-scribe/medical-scribe-constants";
import { selectMedicalScribeRecordingState } from "features/medical-scribe/medical-scribe.slice";
import { CHUNK_LOAD_ERROR_MESSAGE } from "utils/lazy-with-retry.util";
import { reloadPage } from "utils/reload-page.util";

// Long enough not to fire on a slow-but-working load, short enough that nobody
// sits in front of a dead spinner wondering whether scanning is broken.
const SLOW_LOAD_MS = 15000;

const PANEL_HEIGHT = 600;

/**
 * DEV-2806. The dialog used to render a bare spinner with no escape if the load
 * never settled — a request a proxy holds open, or a recovery reload the user
 * declined, both leave `React.lazy` pending forever and memoised on the
 * module-scope component, so reopening the dialog shows the same spinner until the
 * tab is reloaded.
 */
export function ScanStatusPanel({
  title,
  message,
  showReload = true,
  onRetry
}: {
  readonly title: string;
  readonly message?: ReactNode;
  readonly showReload?: boolean;
  readonly onRetry?: () => void;
}) {
  const recordingState = useAppSelector(selectMedicalScribeRecordingState);
  const isRecording = recordingState === RECORDING_STATE_IN_PROGRESS;

  return (
    <Box
      sx={{
        height: PANEL_HEIGHT,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 3
      }}
    >
      <Alert severity="warning" sx={{ maxWidth: 520 }}>
        <Typography variant="body3" fontWeight={700} gutterBottom>
          {title}
        </Typography>
        {message && (
          <Typography variant="body2" sx={{ mb: showReload ? 2 : 0 }}>
            {message}
          </Typography>
        )}
        {showReload && (
          <Typography variant="body2" sx={{ mb: 2 }}>
            {isRecording
              ? // Reloading is the fix, but the Scribe asks the browser to confirm
                // before leaving while a recording is running — so telling someone
                // mid-visit to "just reload" sends them into a prompt they will
                // decline, which is how this state is reached in the first place.
                "Finish or stop your recording, then reload the page to scan."
              : "Reloading the page usually fixes this."}
          </Typography>
        )}
        {/*
          No reload button while recording: `reloadPage` would fire the Scribe's
          own beforeunload prompt, the clinician declines it, and nothing happens.
          Offering a button that is guaranteed to do nothing reproduces the very
          silent no-op this whole panel exists to replace.
        */}
        {showReload && !isRecording && (
          <Button variant="contained" size="small" onClick={reloadPage}>
            Reload page
          </Button>
        )}
        {onRetry && (
          <Button variant="contained" size="small" onClick={onRetry}>
            Try again
          </Button>
        )}
      </Alert>
    </Box>
  );
}

/**
 * Suspense fallback that escalates. Covers the one case no error boundary can: a
 * chunk request that hangs without ever rejecting.
 */
export function ScanLoadingFallback({
  chunkName
}: {
  readonly chunkName: string;
}) {
  const [isSlow, setIsSlow] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setIsSlow(true), SLOW_LOAD_MS);
    return () => clearTimeout(timer);
  }, []);

  if (isSlow) {
    // Names the pending chunk in visible copy: a support ticket arrives as a photo
    // of this panel, and a data-testid does not survive a screenshot.
    return (
      <ScanStatusPanel
        title="Scanning is taking longer than expected"
        message={`Still loading: ${chunkName}.`}
      />
    );
  }

  // The testid keeps the two nested Suspense boundaries distinguishable — which
  // chunk is pending is the only thing a screenshot of this state tells you. It
  // sits on a wrapper because the shared CircularProgress accepts only `sx` and
  // `size`, and that component is out of bounds for this change.
  return (
    <Box data-testid={`scan-loading-${chunkName}`}>
      <CircularProgress sx={{ width: "100%", height: PANEL_HEIGHT }} />
    </Box>
  );
}

export function ScanLoadErrorPanel() {
  // The shared copy, not `error.message` — that reads
  // "Failed to fetch dynamically imported module: https://…/assets/x-abc123.js",
  // which means nothing to a clinic user. The raw text goes to Datadog instead.
  return (
    <ScanStatusPanel
      title="Scanning could not be loaded"
      message={CHUNK_LOAD_ERROR_MESSAGE}
    />
  );
}
